/**
 * Shared event dictionary for the Analytics page.
 *
 * Keeps the `key → { alias, desc, family, active, reason }` mapping in one
 * place so the event legend, feed list, and detail modal all stay in sync.
 *
 * `active`  – true if the event is currently emitted to the /api/v2/analytics
 *             REST endpoint by the frontend. False means the event exists in
 *             the schema but is not currently flowing into the dashboard.
 * `reason`  – populated on inactive events only; explains exactly why the
 *             event is not currently firing (for debugging).
 *
 * `family`  drives a subtle dot color in the feed; the rest of the UI stays
 * monochrome to keep the visual hierarchy calm.
 */

export const EVENT_FAMILY_COLORS = {
  entry: "#94a3b8",
  upload: "#6366f1",
  generation: "#10b981",
  editor: "#8b5cf6",
  save: "#14b8a6",
  share: "#f59e0b",
  account: "#ec4899",
  asset: "#94a3b8",
  other: "#94a3b8",
};

export const EVENT_DICT = [
  // ── Active events (alphabetical by alias) ────────────────────────────────
  {
    key: "account_created",
    alias: "Account Created",
    desc: "User created an account.",
    family: "account",
    active: true,
  },
  {
    key: "gallery_ready",
    alias: "Auto-Furnish Complete",
    desc: "Auto-furnish successfully completed. The furnished experience is now live and editable.",
    family: "generation",
    active: true,
  },
  {
    key: "editor_closed",
    alias: "Returned to Gallery",
    desc: "User clicked Done in the editor and was taken back to the gallery view. Each occurrence is the end of one editor visit; subsequent Editor Opened entries from the same user are a new visit into the same interactive session.",
    family: "editor",
    active: true,
  },
  {
    key: "editor_opened",
    alias: "Editor Opened",
    desc: "User opened the editor to customize a layout.",
    family: "editor",
    active: true,
  },
  {
    key: "floorplan_uploaded",
    alias: "File Submitted",
    desc: "A floor plan file was submitted and sent to the AI pipeline for processing.",
    family: "upload",
    active: true,
  },
  {
    key: "link_copied",
    alias: "Gallery Link Copied",
    desc: "User copied the gallery link to share it, from the editor or the gallery. This is the event counted as a Share.",
    family: "share",
    active: true,
  },
  {
    key: "webview_opened",
    alias: "Gallery Viewed",
    desc: "User viewed a floor plan.",
    family: "entry",
    active: true,
  },
  {
    key: "inquiry_intent",
    alias: "Interest piqued",
    desc: "A viewer tapped the I'm Interested button itself, counted on the button rather than inside the form. `surface` separates gallery from editor; `flow` records which path the tap took: `one_tap` (we hold their email and they have agreed before, so the tap sends), `first_confirm` (we hold their email but this is their first interest, so a confirmation is shown), or `email_form` (no email on file, so the form asks for one). Always read the three flows separately — blended, the completion rate tracks the signed-in/guest mix and how many viewers are on their first interest rather than the funnel. Re-taps after interest is sent are not counted.",
    family: "entry",
    active: true,
  },
  {
    key: "inquiry_modal_opened",
    alias: "Interest form opened",
    desc: "A viewer opened the I'm Interested dialog — either the email form or the one-time confirmation. Never fires on the `one_tap` flow, which sends without opening anything. Pair with Interest captured to see how many viewers opened it and left without sharing contact info. `surface` separates gallery from editor.",
    family: "entry",
    active: true,
  },
  {
    key: "inquiry_submitted",
    alias: "Interest captured",
    desc: "A viewer shared contact info and the server recorded a new inquiry. Counted for viewer-to-interest conversion via EventLog. `flow` matches Interest piqued, so the one-tap, first-confirmation and form completion rates can be read apart. The key stays `inquiry_submitted` so historical rows keep counting.",
    family: "entry",
    active: true,
  },
  {
    key: "add_items_manually",
    alias: "Items Added",
    desc: "User added furniture items manually.",
    family: "editor",
    active: true,
  },
  {
    key: "download_layout",
    alias: "Layout Downloaded",
    desc: "User downloaded the furnished floor plan as an image.",
    family: "share",
    active: true,
  },
  {
    key: "reset_layout",
    alias: "Layout Reset",
    desc: "Canvas was cleared after user adjusted the scale.",
    family: "editor",
    active: true,
  },
  {
    key: "layout_saved",
    alias: "Layout Saved",
    desc: "Guest user made an account to save their edits.",
    family: "save",
    active: true,
  },
  {
    key: "version_duplicated",
    alias: "Layout Version Duplicated",
    desc: "User duplicated an existing layout to create a new version.",
    family: "editor",
    active: true,
  },
  {
    key: "furnish_room",
    alias: "Room Furnished",
    desc: "User auto-furnished a room.",
    family: "editor",
    active: true,
  },
  {
    key: "shuffle_room",
    alias: "Room Shuffled",
    desc: "User requested and received a new layout variation.",
    family: "editor",
    active: true,
  },
  {
    key: "auth_wall_triggered",
    alias: "Sign-Up Prompted",
    desc: "Guest user attempted to save or share before having an account. Indicates they reached the conversion point.",
    family: "account",
    active: true,
  },
  {
    key: "upload_failed",
    alias: "Upload Failed",
    desc: "User submitted a file that the AI pipeline could not process. Error type recorded (e.g. unsupported format, scale validation failure, timeout).",
    family: "upload",
    active: true,
  },

  // ── Inactive events (alphabetical by alias) ───────────────────────────────
  {
    key: "link_shared",
    alias: "Gallery Link Shared",
    desc: "Legacy share event. The current client never emits this — tapping Share just opens the share modal, and the actual share is recorded as Gallery Link Copied when the link is copied. Still counted toward Shares for historical rows.",
    family: "share",
    active: false,
    reason: "Not emitted by the current client; superseded by link_copied. Kept so historical link_shared rows still count as shares.",
  },
];

const EVENT_LOOKUP = EVENT_DICT.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

export const getEventInfo = (key) => {
  if (!key) return { key: "unknown", alias: "Unknown", desc: "Unrecognized event.", family: "other", active: false };
  return EVENT_LOOKUP[key] || { key, alias: key, desc: "Custom or undocumented event.", family: "other", active: false };
};

export const getFamilyColor = (key) => {
  const info = getEventInfo(key);
  return EVENT_FAMILY_COLORS[info.family] || EVENT_FAMILY_COLORS.other;
};
